import React, { ComponentProps, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import dayjs, { Dayjs } from 'dayjs'
import { Button } from '@notino/react-styleguide'
import FocusTrap from 'focus-trap-react'

// types
import { DateTimeSlotPickerBookingFormFieldProps } from './types'

// styles
import * as SC from './DateTimeSlotPickerBookingFormFieldStyles'
import { BREAKPOINTS_INTS } from '../../styles/constants'

// components
import { RadioButtonOption } from '../RadioButtonsFormField/types'
import DateTimeSlotPicker from '../../atoms/DateTimeSlotPicker/DateTimeSlotPicker'
import { DateTimeSlotPickerValueType } from '../../atoms/DateTimeSlotPicker/types'
import Drawer from '../../components/Drawer/Drawer'
import InputLabel from '../../atoms/InputLabel/InputLabel'
import InputTrigger from '../../atoms/InputTrigger/InputTrigger'

// assets
import CalendarIcon from '../../assets/icons/CalendarIcon'

// hooks
import useOnClickOutside from '../../hooks/useClickOutside'
import useMessages from '../../hooks/useMessages'
import useIsMobile from '../../hooks/useIsMobile'

// utils
import { RESERVATIONS_TIME_SLOTS, SALONS_FILTER_TYPE } from '../../utils/enums'
import { RESERVATIONS_TIME_SLOTS_START_TIME, SALONS_FILTER_ITEMS_CONFIG, TIME_SLOTS_TRANSLATIONS } from '../../utils/helper'
import { getIntl } from '../../utils/intl'

type ValueType = DateTimeSlotPickerValueType<RESERVATIONS_TIME_SLOTS>

const formatSubmittedValue = (
	now: Dayjs,
	dayPeriodTranslations: Record<RESERVATIONS_TIME_SLOTS, string | undefined>,
	value: DateTimeSlotPickerBookingFormFieldProps<RESERVATIONS_TIME_SLOTS>['input']['value']
) => {
	const { dateFrom, dateTo, timeSlot } = value
	const { formatMessage, formatDateTimeRange, formatDate } = getIntl()

	let formattedDate
	let formattedDayPeriod
	const isToday = dateTo === undefined && dateFrom && dateFrom?.isSame(now, 'date')
	const isTomorrow = dateTo === undefined && dateFrom && dateFrom?.isSame(now.add(1, 'day'), 'date')

	if (isToday) {
		formattedDate = formatMessage({ id: 'Today', defaultMessage: 'Today' })
	} else if (isTomorrow) {
		formattedDate = formatMessage({ id: 'Tomorrow', defaultMessage: 'Tomorrow' })
	} else if (dateFrom && dateTo === undefined) {
		formattedDate = formatDate(dateFrom.toDate(), { month: 'long', day: 'numeric', weekday: 'long' })
	} else if (dateFrom && dateTo) {
		formattedDate = formatDateTimeRange(dateFrom.toDate(), dateTo.toDate(), { day: 'numeric', month: 'numeric' })
	}

	if (timeSlot !== RESERVATIONS_TIME_SLOTS.ANY) {
		formattedDayPeriod = dayPeriodTranslations[timeSlot]
	}

	if (!formattedDate && !formattedDayPeriod) {
		return dayPeriodTranslations[RESERVATIONS_TIME_SLOTS.ANY]
	}

	return formattedDate ? `${formattedDate}${formattedDayPeriod ? `, ${formattedDayPeriod}` : ''}` : formattedDayPeriod
}

const DateTimeSlotPickerBookingFormField = (props: DateTimeSlotPickerBookingFormFieldProps<RESERVATIONS_TIME_SLOTS>) => {
	const {
		input: { value, onChange },
		maxRange,
		defaultValue: defaultValueProps,
		onFocus,
		label,
		labelScreenReaderOnly,
		...restProps
	} = props

	const { messages } = useMessages()
	const intl = useIntl()
	const { formatMessage } = intl
	const isMobile = useIsMobile(BREAKPOINTS_INTS.sm)
	const labelId = useId()
	const dialogId = useId()

	// save current date into a state, so we get consistent results when working with this variable
	const [now, setNow] = useState(dayjs())

	const [internalValue, setInternalValue] = useState<ValueType>({
		dateFrom: value.dateFrom,
		dateTo: value.dateTo,
		timeSlot: value.timeSlot
	})

	const [isMenuOpen, setIsMenuOpen] = useState(false)

	const defaultValue = useMemo(() => {
		return defaultValueProps === 'now'
			? {
					timeSlot: RESERVATIONS_TIME_SLOTS.ANY,
					dateFrom: now,
					dateTo: undefined
				}
			: defaultValueProps
	}, [defaultValueProps, now])

	const wrapperRef = useRef<HTMLDivElement | null>(null)
	const triggerRef = useRef<HTMLButtonElement | null>(null)
	const wasMenuOpenRef = useRef(false)

	const isToday = internalValue.dateFrom?.isSame(now, 'date') && internalValue.dateTo === undefined
	const afternoon = dayjs(new Date(now.year(), now.month(), now.date(), RESERVATIONS_TIME_SLOTS_START_TIME.AFTERNOON, 0))
	const evening = dayjs(new Date(now.year(), now.month(), now.date(), RESERVATIONS_TIME_SLOTS_START_TIME.EVENING, 0))

	const hasSubmittedValue = value.dateFrom !== undefined || value.dateTo !== undefined || value.timeSlot !== RESERVATIONS_TIME_SLOTS.ANY

	const isRangeError =
		internalValue.dateFrom && internalValue.dateTo && maxRange !== undefined && !(internalValue.dateTo.diff(internalValue.dateFrom, 'day') <= maxRange)

	const closeMenu = () => {
		setIsMenuOpen(false)
	}

	useOnClickOutside([wrapperRef], () => {
		if (!isMobile && isMenuOpen) {
			closeMenu()
		}
	})

	useEffect(() => {
		// synchronize submitted values with internal values
		let newValue: ValueType = {
			dateFrom: value.dateFrom,
			dateTo: value.dateTo,
			timeSlot: value.timeSlot
		}

		if (defaultValue && !value.dateFrom && !value.dateTo) {
			newValue = defaultValue
		}

		setInternalValue(newValue)
	}, [value.dateFrom, value.dateTo, value.timeSlot, defaultValue?.dateFrom, defaultValue?.dateTo, defaultValue?.timeSlot, defaultValue, now])

	useEffect(() => {
		// update "now" whenever menu is opened
		if (isMenuOpen) {
			setNow(dayjs())
		}
	}, [isMenuOpen])

	useEffect(() => {
		if (wasMenuOpenRef.current && !isMenuOpen) {
			triggerRef.current?.focus()
		}
		wasMenuOpenRef.current = isMenuOpen
	}, [isMenuOpen])

	useEffect(() => {
		// close mobile drawer using Escape (desktop uses focus-trap escapeDeactivates)
		if (!isMenuOpen || !isMobile) {
			return undefined
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeMenu()
			}
		}

		document.addEventListener('keydown', handleKeyDown)

		return () => {
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [isMenuOpen, isMobile])

	const dayPeriodTranslations = TIME_SLOTS_TRANSLATIONS()

	const getOptions = () => {
		const result: RadioButtonOption<RESERVATIONS_TIME_SLOTS>[] = [
			{
				id: RESERVATIONS_TIME_SLOTS.ANY,
				label: dayPeriodTranslations[RESERVATIONS_TIME_SLOTS.ANY]
			}
		]

		if ((isToday && now.isBefore(afternoon)) || !isToday) {
			result.push({
				id: RESERVATIONS_TIME_SLOTS.MORNING,
				label: dayPeriodTranslations[RESERVATIONS_TIME_SLOTS.MORNING],
				description: formatMessage({ id: 'Until { time }', defaultMessage: 'Until { time }' }, { time: afternoon.format('LT') })
			})
		}

		if ((isToday && now.isBefore(evening)) || !isToday) {
			result.push({
				id: RESERVATIONS_TIME_SLOTS.AFTERNOON,
				label: dayPeriodTranslations[RESERVATIONS_TIME_SLOTS.AFTERNOON],
				description: formatMessage({ id: 'Until { time }', defaultMessage: 'Until { time }' }, { time: evening.format('LT') })
			})
		}

		result.push({
			id: RESERVATIONS_TIME_SLOTS.EVENING,
			label: dayPeriodTranslations[RESERVATIONS_TIME_SLOTS.EVENING],
			description: formatMessage({ id: 'After { time }', defaultMessage: 'After { time }' }, { time: evening.format('LT') })
		})

		return result
	}

	const timeSlotOptions = getOptions()

	const onConfirm = () => {
		onChange(internalValue)
		closeMenu()
	}

	const onDateTimeSlotPickerChange: ComponentProps<typeof DateTimeSlotPicker<RESERVATIONS_TIME_SLOTS>>['onChange'] = (newValue) => {
		let validValue = {
			...newValue
		}

		if (isToday) {
			if (
				(internalValue.timeSlot === RESERVATIONS_TIME_SLOTS.AFTERNOON && now.isAfter(evening)) ||
				(internalValue.timeSlot === RESERVATIONS_TIME_SLOTS.MORNING && now.isAfter(afternoon))
			) {
				validValue = {
					timeSlot: RESERVATIONS_TIME_SLOTS.ANY,
					dateFrom: internalValue.dateFrom,
					dateTo: internalValue.dateTo
				}
			}
		}

		setInternalValue(validValue)
	}

	const onToggleMenu = () => {
		setIsMenuOpen((open) => !open)
	}

	const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		// Match Select behavior: ArrowDown / ArrowUp open the popup when the trigger is focused
		if (isMenuOpen || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) {
			return
		}

		event.preventDefault()
		setIsMenuOpen(true)
	}

	const handleDrawerAfterOpenChange = (isOpen: boolean) => {
		if (!isOpen) {
			return
		}

		// Wait for drawer open animation / portal mount, then move focus into the panel
		window.setTimeout(() => {
			const focusedDay = document.querySelector<HTMLElement>('[data-datepicker-focused-day="true"]')
			if (focusedDay) {
				focusedDay.focus()
				return
			}

			document.querySelector<HTMLElement>('[data-drawer-close]')?.focus()
		}, 0)
	}

	const handleClearSubmittedValue: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		e.stopPropagation()
		onChange({ dateFrom: undefined, dateTo: undefined, timeSlot: RESERVATIONS_TIME_SLOTS.ANY })
	}

	const getFooterExtraContent = () => {
		const selectedDateTitle = SALONS_FILTER_ITEMS_CONFIG(undefined, undefined)[SALONS_FILTER_TYPE.DATE].getSelectedTitle(
			internalValue.timeSlot,
			internalValue.dateFrom,
			internalValue.dateTo
		)

		return selectedDateTitle ? <SC.DateFilterFooterLabel>{selectedDateTitle}</SC.DateFilterFooterLabel> : null
	}

	const extraContent = getFooterExtraContent()

	const dialogLabel = formatMessage({ id: 'Choose date and time', defaultMessage: 'Choose date and time' })

	const datePicker = (
		<DateTimeSlotPicker
			value={internalValue}
			onChange={onDateTimeSlotPickerChange}
			layout={'row'}
			size={'small'}
			timeSlotOptions={timeSlotOptions}
			calendarErrorMsg={
				isRangeError
					? formatMessage(
							{ id: 'The selected range exceeds { daysCount }', defaultMessage: 'The selected range exceeds { daysCount }' },
							{ daysCount: maxRange }
						)
					: undefined
			}
			{...restProps}
		/>
	)

	const confirmButton = (
		<Button fullWidth onClick={onConfirm} disabled={isRangeError || !internalValue.dateFrom} type={'button'}>
			{messages.Confirm}
		</Button>
	)

	return (
		<SC.Wrapper ref={wrapperRef}>
			<InputLabel id={labelId} screenReaderOnly={labelScreenReaderOnly}>
				{label}
			</InputLabel>
			<InputTrigger
				ref={triggerRef}
				labelName={label}
				labelledBy={labelId}
				value={formatSubmittedValue(now, dayPeriodTranslations, value)}
				prefixIcon={<CalendarIcon />}
				allowClear={hasSubmittedValue}
				fieldStyle={'no-borders'}
				expanded={isMenuOpen}
				hasPopup={'dialog'}
				controlsId={dialogId}
				onClick={(e) => {
					e.stopPropagation()
					onToggleMenu()
				}}
				onFocus={onFocus}
				onKeyDown={handleTriggerKeyDown}
				onClear={handleClearSubmittedValue}
			/>
			{isMenuOpen && !isMobile && (
				<FocusTrap
					active
					focusTrapOptions={{
						escapeDeactivates: true,
						returnFocusOnDeactivate: false,
						onDeactivate: closeMenu,
						initialFocus: () => {
							const focusedDay = wrapperRef.current?.querySelector<HTMLElement>('[data-datepicker-focused-day="true"]')
							return focusedDay || (wrapperRef.current?.querySelector<HTMLElement>('[role="dialog"]') as HTMLElement)
						},
						fallbackFocus: () => wrapperRef.current as HTMLElement
					}}
				>
					<SC.Dropdown id={dialogId} role={'dialog'} aria-label={dialogLabel} aria-modal={'true'} tabIndex={-1}>
						{datePicker}
						{confirmButton}
					</SC.Dropdown>
				</FocusTrap>
			)}
			{isMobile && (
				<Drawer
					open={isMenuOpen}
					mask
					maskClosable
					title={messages.Date}
					onClose={closeMenu}
					afterOpenChange={handleDrawerAfterOpenChange}
					footer={
						<SC.DrawerFooterWrapper>
							{extraContent && <SC.FooterExtraContent>{extraContent}</SC.FooterExtraContent>}
							<SC.FooterCtaButtonWrapper>{confirmButton}</SC.FooterCtaButtonWrapper>
						</SC.DrawerFooterWrapper>
					}
					destroyOnClose
				>
					<SC.DrawerContentWrapper id={dialogId}>{datePicker}</SC.DrawerContentWrapper>
				</Drawer>
			)}
		</SC.Wrapper>
	)
}

export default DateTimeSlotPickerBookingFormField
